import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import {
  catalogBasePath,
  catalogConfirmationPath,
  catalogOrderStatusPath,
} from "./utils/publicCatalogRoutes";
import { Toaster } from "react-hot-toast";
import { QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AuthProviderV2 } from "./contexts/AuthContextV2";
import { getPostLoginPath } from "./utils/collaboratorAccess";
import { peekPostLoginRedirect } from "./utils/postLoginRedirect";
import { LanguageProvider } from "./contexts/LanguageContext";
import { GoogleAuthProvider } from "./contexts/GoogleAuthContext";
import { AutoReconnectionNotification } from "./components/AutoReconnectionNotification";
import { DelideskEmbedSessionGate } from "./components/DelideskEmbedSessionGate";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { queryClient } from "./services/api/queryClient";
import { warnDeprecatedSecrets } from "./services/secureMigrationNotice";
import { isDelideskEmbed } from "./utils/delideskEmbed";
import "./index.css";

warnDeprecatedSecrets();

// Code-splitting: cada rota grande vira chunk próprio (todo `front-deep-cleanup`).
const Login = lazy(() =>
  import("./pages/Login").then((m) => ({ default: m.Login }))
);
const Dashboard = lazy(() =>
  import("./pages/Dashboard").then((m) => ({ default: m.Dashboard }))
);
const ColaboradorLayout = lazy(() =>
  import("./pages/colaborador/ColaboradorLayout").then((m) => ({
    default: m.ColaboradorLayout,
  }))
);
const DevShell = lazy(() =>
  import("./pages/dev/DevShell").then((m) => ({ default: m.DevShell }))
);
const Setup = lazy(() =>
  import("./pages/Setup").then((m) => ({ default: m.Setup }))
);
const Onboarding = lazy(() =>
  import("./pages/Onboarding").then((m) => ({ default: m.Onboarding }))
);
const ClientScheduling = lazy(() => import("./pages/ClientScheduling"));
const PublicScheduling = lazy(() => import("./pages/PublicScheduling"));
const PublicOrders = lazy(() => import("./pages/PublicOrders"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));
const OrderStatus = lazy(() => import("./pages/OrderStatus"));
const ContractPlanPage = lazy(() =>
  import("./pages/ContractPlanPage").then((m) => ({ default: m.ContractPlanPage }))
);
const ContractPixPage = lazy(() =>
  import("./pages/ContractPixPage").then((m) => ({ default: m.ContractPixPage }))
);
const GoogleCallback = lazy(() =>
  import("./pages/GoogleCallback").then((m) => ({ default: m.GoogleCallback }))
);
const AuthorizeDevicePage = lazy(() =>
  import("./pages/AuthorizeDevicePage").then((m) => ({
    default: m.AuthorizeDevicePage,
  }))
);
const DelideskSsoPage = lazy(() =>
  import("./pages/DelideskSsoPage").then((m) => ({
    default: m.DelideskSsoPage,
  }))
);
const LegalPage = lazy(() => import("./pages/LegalPage"));
const OrderClaimPresentePage = lazy(() => import("./pages/OrderClaimPresentePage"));
const CourierDispatchPage = lazy(() =>
  import("./pages/CourierDispatchPage").then((m) => ({
    default: m.CourierDispatchPage,
  }))
);

const RouteFallback: React.FC = () => (
  <div className="h-screen flex items-center justify-center bg-gradient-delivai">
    <div className="loading-spinner"></div>
  </div>
);

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <RouteFallback />;
  }
  if (!user) {
    // Embed DeliDesk: nunca redirecionar para o login CNPJ do site.
    if (isDelideskEmbed()) {
      return <DelideskEmbedSessionGate />;
    }
    const next = `${location.pathname}${location.search || ""}`;
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(next)}`}
        replace
      />
    );
  }
  return (
    <GoogleAuthProvider>
      {children}
      <AutoReconnectionNotification />
    </GoogleAuthProvider>
  );
};

/** Link de primeiro acesso / convite: deve abrir o login do colaborador mesmo com sessão da empresa. */
function isCollaboratorInvitePath(pathname: string, search: string): boolean {
  if (pathname !== "/login") return false;
  const q = new URLSearchParams(search);
  return q.get("colaborador") === "1" || q.get("colaboradores") === "1";
}

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const allowLoginDespiteSession = isCollaboratorInvitePath(
    location.pathname,
    location.search
  );
  if (loading) {
    return <RouteFallback />;
  }
  if (user && !allowLoginDespiteSession) {
    // DeliDesk / sessão expirada: /login?redirect=/autorizar… não pode ir ao painel.
    const redirect = peekPostLoginRedirect(
      new URLSearchParams(location.search).get("redirect")
    );
    if (redirect) {
      return <Navigate to={redirect} replace />;
    }
    const target = getPostLoginPath(user);
    if (target === "/login" || target === location.pathname) {
      return <>{children}</>;
    }
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
};

/** Links antigos `/orders/:cnpj` → cardápio público `/catalog/:cnpj`. */
const LegacyPublicCatalogRedirect: React.FC<{
  to: (companyName: string) => string;
}> = ({ to }) => {
  const { companyName } = useParams<{ companyName: string }>();
  if (!companyName) return <Navigate to="/" replace />;
  return <Navigate to={to(companyName)} replace />;
};

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route path="/setup" element={<Setup />} />
        <Route
          path="/agendamento/:companyId"
          element={
            <GoogleAuthProvider>
              <ClientScheduling />
            </GoogleAuthProvider>
          }
        />
        <Route
          path="/schedule"
          element={
            <GoogleAuthProvider>
              <ClientScheduling />
            </GoogleAuthProvider>
          }
        />
        <Route
          path="/dashboard/agendamentos/:cnpj"
          element={
            <GoogleAuthProvider>
              <PublicScheduling />
            </GoogleAuthProvider>
          }
        />
        {/* QR iFood — presente / claim telefone (público) */}
        <Route path="/presente/:token" element={<OrderClaimPresentePage />} />
        <Route path="/despacho" element={<CourierDispatchPage />} />
        {/* Cardápio público (catalog) — painel interno permanece em /dashboard/orders */}
        <Route path="/catalog/:companyName" element={<PublicOrders />} />
        <Route
          path="/catalog/:companyName/confirmation"
          element={<OrderConfirmation />}
        />
        <Route
          path="/catalog/:companyName/status-pedido"
          element={<OrderStatus />}
        />
        {/* Legado: /orders/:cnpj → /catalog/:cnpj */}
        <Route
          path="/orders/:companyName"
          element={<LegacyPublicCatalogRedirect to={catalogBasePath} />}
        />
        <Route
          path="/orders/:companyName/confirmation"
          element={<LegacyPublicCatalogRedirect to={catalogConfirmationPath} />}
        />
        <Route
          path="/orders/:companyName/status-pedido"
          element={<LegacyPublicCatalogRedirect to={catalogOrderStatusPath} />}
        />
        <Route
          path="/google-callback"
          element={
            <GoogleAuthProvider>
              <GoogleCallback />
            </GoogleAuthProvider>
          }
        />
        <Route path="/autorizar" element={<AuthorizeDevicePage />} />
        <Route path="/authorize-device" element={<Navigate to="/autorizar" replace />} />
        <Route path="/delidesk-sso" element={<DelideskSsoPage />} />
        <Route path="/privacidade/usuarios" element={<LegalPage doc="privacidade-usuarios" />} />
        <Route path="/privacidade/empresas" element={<LegalPage doc="privacidade-empresas" />} />
        <Route path="/privacidade" element={<Navigate to="/privacidade/usuarios" replace />} />
        <Route path="/termos/empresas" element={<LegalPage doc="termos-empresas" />} />
        <Route path="/termos/usuarios" element={<LegalPage doc="termos-usuarios" />} />
        <Route path="/termos/agendamento" element={<LegalPage doc="termos-agendamento" />} />
        <Route path="/termos/colaboradores" element={<LegalPage doc="termos-colaboradores" />} />
        <Route path="/termos/desenvolvedores" element={<LegalPage doc="termos-desenvolvedores" />} />
        <Route path="/termos" element={<Navigate to="/termos/empresas" replace />} />
        <Route path="/termos-consumidor" element={<Navigate to="/termos/usuarios" replace />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/pagamento/contrato" element={<ContractPlanPage />} />
        <Route path="/pagamento/contrato/pix" element={<ContractPixPage />} />
        <Route
          path="/dashboard/*"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/colaborador/*"
          element={
            <ProtectedRoute>
              <ColaboradorLayout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dev/*"
          element={
            <ProtectedRoute>
              <DevShell />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/delivai/*"
          element={<Navigate to="/dev" replace />}
        />
        <Route path="/" element={<Navigate to="/dashboard/delivery" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AuthProviderV2>
            <LanguageProvider>
              <Router>
                <AppRoutes />
                <Toaster
                  position="top-center"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: "#1f2937",
                      color: "#fff",
                      borderRadius: "12px",
                      fontSize: "15px",
                      padding: "16px 20px",
                      margin: "8px",
                      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      maxWidth: "500px",
                      minWidth: "300px",
                    },
                    success: {
                      duration: 4000,
                      iconTheme: {
                        primary: "#4ade80",
                        secondary: "#fff",
                      },
                      style: {
                        background: "#065f46",
                        border: "1px solid #4ade80",
                      },
                    },
                    error: {
                      duration: 5000,
                      iconTheme: {
                        primary: "#ef4444",
                        secondary: "#fff",
                      },
                      style: {
                        background: "#7f1d1d",
                        border: "1px solid #ef4444",
                      },
                    },
                  }}
                />
              </Router>
            </LanguageProvider>
          </AuthProviderV2>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
